import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/app/core/services/auth.service';
import { PermitTaskService } from '@/app/core/services/permit-task.service';
import { AppPermission } from '@/app/core/shared/constants';

@Component({
  selector: 'app-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `
    <ul class="layout-menu">
      @for (item of model(); track item.label) {
        @if (!item.separator) {
          <li app-menuitem [item]="item" [root]="true"></li>
        } @else {
          <li class="menu-separator"></li>
        }
      }
    </ul>
  `,
})
export class AppMenu {
  private authService = inject(AuthService);
  private taskService = inject(PermitTaskService);

  constructor() {
    // Проб живе тут, а не в `model`: `computed` мусить лишатись чистим, інакше кожен
    // перерахунок меню слав би запит. Сервіс кличе мережу рівно раз на застосунок.
    this.taskService.probeOperatorKinds();
  }

  model = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        label: 'Головна',
        items: [
          { label: 'Панель приладів', icon: 'pi pi-fw pi-home', routerLink: ['/home'] }
        ]
      }
    ];

    const orgItems: MenuItem[] = [];

    if (this.authService.hasPermission(AppPermission.EMPLOYEES_VIEW)) {
      orgItems.push({ label: 'Співробітники', icon: 'pi pi-fw pi-users', routerLink: ['/employee'] });
    }

    if (this.authService.hasPermission(AppPermission.DEPARTMENTS_VIEW)) {
      orgItems.push({ label: 'Департаменти', icon: 'pi pi-fw pi-building', routerLink: ['/department'] });
    }

    if (this.authService.hasPermission(AppPermission.ORGANIZATIONS_VIEW)) {
      orgItems.push({ label: 'Організації', icon: 'pi pi-fw pi-sitemap', routerLink: ['/organization'] });
    }

    if (this.authService.hasPermission(AppPermission.SUPPLIERS_VIEW)) {
      orgItems.push({ label: 'Постачальники', icon: 'pi pi-fw pi-truck', routerLink: ['/supplier'] });
    }

    if (this.authService.hasPermission(AppPermission.BUYERS_VIEW)) {
      orgItems.push({ label: 'Покупці', icon: 'pi pi-fw pi-shopping-bag', routerLink: ['/buyer'] });
    }

    if (orgItems.length > 0) {
      items.push({
        label: 'Організація',
        items: orgItems
      });
    }

    const financeItems: MenuItem[] = [];

    if (this.authService.hasPermission(AppPermission.CASH_TRANSACTIONS_VIEW)) {
      financeItems.push(
        { label: 'Готівковий баланс', icon: 'pi pi-fw pi-wallet', routerLink: ['/department-balances'] },
        { label: 'Рахунки', icon: 'pi pi-fw pi-credit-card', routerLink: ['/department-accounts'] },
        { label: 'Облік готівки', icon: 'pi pi-fw pi-money-bill', routerLink: ['/cash-transactions'] }
      );
    }

    if (this.authService.hasPermission(AppPermission.EXCHANGE_RATES_VIEW)) {
      financeItems.push(
        { label: 'Курси валют', icon: 'pi pi-fw pi-chart-line', routerLink: ['/exchange-rates'] }
      );
    }

    if (financeItems.length > 0) {
      items.push({
        label: 'Фінанси',
        items: financeItems
      });
    }

    const documentItems: MenuItem[] = [];

    if (this.authService.hasPermission(AppPermission.DOCUMENTS_RECEIPT_INVOICES_VIEW)) {
      documentItems.push({
        label: 'Прибуткові накладні',
        icon: 'pi pi-fw pi-file-import',
        routerLink: ['/receipt-invoices']
      });
    }

    if (this.authService.hasPermission(AppPermission.DOCUMENTS_EXPENSE_INVOICES_VIEW)) {
      documentItems.push({
        label: 'Видаткові накладні',
        icon: 'pi pi-fw pi-file-export',
        routerLink: ['/expense-invoices'],
      });
    }

    if (this.authService.hasPermission(AppPermission.DOCUMENTS_BUYER_RETURNS_VIEW)) {
      documentItems.push({
        label: 'Повернення від покупців',
        icon: 'pi pi-fw pi-undo',
        routerLink: ['/buyer-returns'],
      });
    }

    if (this.authService.hasPermission(AppPermission.DOCUMENTS_REVALUATION_ACTS_VIEW)) {
      documentItems.push({
        label: 'Акти переоцінки',
        icon: 'pi pi-fw pi-tag',
        routerLink: ['/revaluation-acts'],
      });
    }

    if (documentItems.length > 0) {
      items.push({
        label: 'Документи',
        items: documentItems
      });
    }

    if (this.authService.hasPermission(AppPermission.REPAIR_REQUESTS_VIEW)) {
      items.push({
        label: 'Сервіс',
        items: [
          { label: 'Ремонти', icon: 'pi pi-fw pi-wrench', routerLink: ['/repair-requests'] }
        ]
      });
    }

    // Заявки та позиції дозволів фільтруються департаментом співробітника: без
    // нього сервер відповідає `403 User is not assigned to any department` на
    // кожен запит. Тому ховаємо розділ по `employee.department_id`, а не по ролі —
    // оператор без департаменту працює тільки з дошкою завдань.
    //
    // `manage` — виняток, і він у самій спеці: «Працівник без департаменту **й без
    // `manage`** → 403». Адміністратор без прив'язки до магазину бачить усі заявки,
    // тож ховати від нього розділ означало б захищатись від помилки, якої не буде.
    const hasDepartment =
      !!this.authService.currentUser()?.employee?.department_id ||
      this.authService.hasPermission(AppPermission.PERMIT_REQUESTS_MANAGE);

    const permitItems: MenuItem[] = [];

    if (this.authService.hasPermission(AppPermission.PERMIT_REQUESTS_VIEW) && hasDepartment) {
      permitItems.push(
        { label: 'Заявки на дозволи', icon: 'pi pi-fw pi-id-card', routerLink: ['/permit-requests'] },
        // «(перегляд)» у назві — не косметика: сусідній пункт нижче веде на дошку, де кнопки
        // переходів є, і називається майже так само. Без підпису різницю між ними видно
        // тільки після кліку — і читається вона як «на цій дошці нічого не працює».
        { label: 'Дошка дозволів (перегляд)', icon: 'pi pi-fw pi-th-large', routerLink: ['/permit-board'] }
      );
    }

    // Звіти — НЕ той самий гейт, що в двох сусідів вище, і це зміна бекенду від 21.09:
    // обидва роути `/permit-reports/*` закриті на `permit_requests.manage`, тобто сьогодні
    // на `admin`. Продавець отримує `403` **у тому числі по власному магазину** — підстановки
    // власного магазину більше немає ні для кого. Під `VIEW` пункт лишався б видимим саме
    // тим, кому екран тепер відповідає помилкою.
    if (this.authService.hasPermission(AppPermission.PERMIT_REQUESTS_MANAGE)) {
      permitItems.push(
        { label: 'Звіти по дозволах', icon: 'pi pi-fw pi-chart-bar', routerLink: ['/permit-reports'] }
      );
    }

    // Дошка оператора — поза перевіркою департаменту (скоуп `operator` наскрізний по
    // магазинах) І поза перевіркою дозволів. Останнє — вимога §3.4: `/permit-tasks/*`
    // свідомо не має `requirePermission`, доступ дає рядок у `permit_operators`. Фронт
    // не може будувати це меню зі списку дозволів; єдине джерело правди —
    // `GET /permit-tasks/kinds`, і саме його читає `operatorKinds()`.
    if (this.taskService.operatorKinds().length > 0) {
      permitItems.push(
        { label: 'Дошка оператора', icon: 'pi pi-fw pi-check-square', routerLink: ['/permit-operator-board'] }
      );
    }

    // Довідник операторів — адмінський, департаменту не має взагалі (§3.4), тому
    // теж поза перевіркою `hasDepartment`.
    if (this.authService.hasPermission(AppPermission.PERMIT_REQUESTS_MANAGE)) {
      permitItems.push(
        { label: 'Оператори дозволів', icon: 'pi pi-fw pi-users', routerLink: ['/permit-operators'] }
      );
    }

    if (permitItems.length > 0) {
      items.push({ label: 'Дозволи', items: permitItems });
    }

    const directoryItems: MenuItem[] = [];

    if (this.authService.hasPermission(AppPermission.PRODUCTS_VIEW)) {
      directoryItems.push(
        { label: 'Бренди', icon: 'pi pi-fw pi-tag', routerLink: ['/brands'] }
      );
    }

    if (this.authService.hasPermission(AppPermission.PRODUCT_CATEGORIES_VIEW)) {
      directoryItems.push(
        { label: 'Категорії товарів', icon: 'pi pi-fw pi-tags', routerLink: ['/product-categories'] }
      );
    }

    if (this.authService.hasPermission(AppPermission.PRODUCTS_VIEW)) {
      directoryItems.push(
        { label: 'Товари', icon: 'pi pi-fw pi-box', routerLink: ['/products'] },
        { label: 'Залишки', icon: 'pi pi-fw pi-database', routerLink: ['/stock-balances'] },
        { label: 'Партії', icon: 'pi pi-fw pi-clone', routerLink: ['/stock-batches'] }
      );
    }

    if (directoryItems.length > 0) {
      items.push({
        label: 'Довідники',
        items: directoryItems
      });
    }

    if (this.authService.hasPermission(AppPermission.MIGRATION_MANAGE)) {
      items.push({
        label: 'Міграція',
        items: [
          { label: 'Огляд міграції', icon: 'pi pi-fw pi-sync', routerLink: ['/migration'] }
        ]
      });
    }

    if (this.authService.hasPermission(AppPermission.SYSTEM_MANAGE)) {
      items.push({
        label: 'Адміністрування',
        items: [
          { label: 'Системні налаштування', icon: 'pi pi-fw pi-cog', routerLink: ['/system-settings'] }
        ]
      });
    }

    return items;
  });
}
